<template>
  <v-dialog v-model="addDialog" class="the-app-dialogs" persistent max-width="600px">
    <v-card>
      <form @submit.prevent="validateBeforeSubmit($event)">
        <v-layout row class="no-select" style="background-color:#09132f">
          <h3 id="dialog-title" class="text-uppercase px-3 pt-3" style="text-xs-center letter-spacing:2px">
            <v-layout align-center>
              <v-icon light x-large class="rgba(0,0,0,0.3)--text pb-1">attach_money</v-icon>
              <span class="px-3">{{ $t('Add New Promo Card') }}</span>
            </v-layout>
          </h3>
          <v-spacer />
          <v-card-actions>
            <v-btn fab icon dark @click="$emit('hidemodal')">
              <v-icon>close</v-icon>
            </v-btn>
          </v-card-actions>
        </v-layout>

        <v-card-title class="grey lighten-3">
          <v-layout column>
            <v-flex xs12 class="headline no-select pb-1">
              <v-layout justify-end></v-layout>
            </v-flex>

            <v-layout row wrap>
              <v-flex pa-2>
                <v-autocomplete
                  id="promo-card-focus-me"
                  v-model="form.theCustomer"
                  :items="customersList"
                  :label="`${$t('Customer')} *`"
                  box
                  hide-details
                  background-color="white"
                  class="text-capitalize"
                  :error="errors.has('Customer')"
                  data-vv-name="Customer"
                  v-validate="'required'"
                />
              </v-flex>

              <v-flex xs1 pt-2 v-if="hasPermissions(['customers_store'])">
                <v-tooltip top>
                  <v-btn label @click="$emit('add-customer')" slot="activator" icon fab small color="green">
                    <v-icon color="white" size="25">add</v-icon>
                  </v-btn>
                  <span>{{ $t('Add new customer') }}</span>
                </v-tooltip>
              </v-flex>

              <v-flex xs4 pa-2>
                <v-text-field
                  v-model="form.theDiscount"
                  :label="`${$t('Discount')} *`"
                  box
                  suffix="%"
                  hide-details
                  background-color="white"
                  :error="errors.has('Discount')"
                  data-vv-name="Discount"
                  v-validate="'required|numeric'"
                />
              </v-flex>

              <v-flex xs12 pa-2>
                <v-autocomplete
                  v-model="form.promo_card_outlets"
                  :items="outletsList"
                  :label="`${$t('Outlet')} *`"
                  box
                  hide-details
                  multiple
                  chips
                  deletable-chips
                  background-color="white"
                  class="text-capitalize"
                  :error="errors.has('Outlet')"
                  data-vv-name="Outlet"
                  v-validate="'required'"
                  :menu-props="{ transition: 'slide-y-transition' }"
                >
                  <template v-slot:selection="{ item, index }">
                    <v-chip v-if="index < 3">
                      <span>{{ item.text }}</span>
                    </v-chip>
                    <span v-if="index === 3" class="grey--text caption"
                      >(+{{ form.promo_card_outlets.length - 3 }} others)</span
                    >
                  </template>
                  <template v-slot:prepend-item>
                    <v-list-tile ripple @click="toggle">
                      <v-list-tile-action>
                        <v-icon
                          :color="
                            form.promo_card_outlets != null
                              ? form.promo_card_outlets.length > 0
                                ? 'indigo darken-4'
                                : ''
                              : ''
                          "
                          >{{ icon }}</v-icon
                        >
                      </v-list-tile-action>
                      <v-list-tile-content>
                        <v-list-tile-title>Select All</v-list-tile-title>
                      </v-list-tile-content>
                    </v-list-tile>
                    <v-divider />
                  </template>
                </v-autocomplete>
              </v-flex>
            </v-layout>
          </v-layout>
        </v-card-title>
        <v-divider light />

        <v-card-actions class="grey lighten-3">
          <v-layout column>
            <v-flex xs12 class="pb-2 px-2" style="color: #444"
              >- {{ $t('Fields marked with asterisk ( * ) are required.') }}</v-flex
            >

            <v-layout justify-end align-center pr-2 pl-2>
              <v-checkbox color="primary" v-model="keepForm" :label="$t('Keep this form to add another one.')" />

              <v-btn outline color="primary" @click="$emit('hidemodal')">{{ $t('Cancel') }}</v-btn>
              <v-btn type="submit" color="success" :loading="btnAddLoading">
                <v-icon :right="$vuetify.rtl" :left="!$vuetify.rtl">save</v-icon>
                {{ $t('Add') }}
              </v-btn>
            </v-layout>
          </v-layout>
        </v-card-actions>
      </form>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import * as helpers from '@/mixins/helperFunctions.js'

export default {
  name: 'add-component',

  props: {
    addDialog: { type: Boolean, required: true }
  },

  data() {
    return {
      btnAddLoading: false,
      form: {
        promo_card_outlets: []
      },
      keepForm: false
    }
  },

  computed: {
    ...mapGetters({
      outletsList: 'outlets/getFullOutletsList',
      customersList: 'customers/getFullCustomersList'
    }),

    selectAllOutlet() {
      if (this.form.promo_card_outlets != null && this.outletsList.length != null) {
        return this.form.promo_card_outlets.length === this.outletsList.length
      }
    },

    selectSomeOutlet() {
      if (this.form.promo_card_outlets != null) {
        return this.form.promo_card_outlets.length > 0 && !this.selectAllOutlet
      }
    },

    icon() {
      if (this.selectAllOutlet) return 'mdi-close-box'
      if (this.selectSomeOutlet) return 'mdi-minus-box'
      return 'mdi-checkbox-blank-outline'
    }
  },

  watch: {
    addDialog: {
      immediate: false,
      handler() {
        if (this.addDialog) {
          this.addEscKeyListener()
          this.resetForm()
          this.keepForm = false
        }
      }
    }
  },

  methods: {
    ...mapActions({
      addPromoCard: 'promoCards/addPromoCard'
    }),

    hasPermissions(permissions) {
      return helpers.userHasPermissions(permissions)
    },

    addEscKeyListener() {
      document.addEventListener('keyup', e => {
        // if the 'ESC' button was pressed and this dialog is shown, hide it
        if (e.keyCode === 27 && this.addDialog) {
          this.$emit('hidemodal')
        }
      })
    },

    resetForm() {
      this.$validator.pause()
      for (let key in this.form) {
        this.form[`${key}`] = null
      }
      this.$nextTick(() => {
        this.$validator.resume()
        // default veutify autofocus only one time works so we have to handled in this way.
        // document.getElementById('promo-card-focus-me').focus()
      })
    },

    toggle() {
      this.$nextTick(() => {
        if (this.selectAllOutlet) {
          this.form.promo_card_outlets = []
        } else {
          this.form.promo_card_outlets = this.outletsList.slice()
        }
      })
    },

    validateBeforeSubmit(event) {
      this.$validator
        .validateAll()
        .then(() => {
          if (!this.errors.any()) {
            this.btnAddLoading = true

            let formData = new FormData(event.target)
            formData.append('customer_id_fk', this.form.theCustomer)
            formData.append('promo_card_discount', this.form.theDiscount)

            if (this.selectAllOutlet) {
              this.form.promo_card_outlets.forEach((outelet, index) => {
                formData.append('promo_card_outlets[' + [index] + ']', outelet.value)
              })
            } else {
              this.form.promo_card_outlets.forEach((outelet, index) => {
                formData.append('promo_card_outlets[' + [index] + ']', outelet)
              })
            }

            this.addPromoCard(formData)
              .then(() => {
                this.btnAddLoading = false
                this.allOutlet = false
                this.$emit('reset-datatable')
                if (this.keepForm) {
                  this.resetForm()
                } else {
                  this.$emit('hidemodal')
                }
              })
              .catch(() => {
                this.btnAddLoading = false
              })
          }
        })
        .catch(() => {
          this.btnAddLoading = false
        })
    }
  }
}
</script>

<style scoped />
